/**
 * PKN Early Access Lounge
 * ---------------------------------------------------------------------------
 * Drives the password page built by sections/PKN-early-access-lounge.liquid:
 *
 *   • countdown  – ticks down to the merchant supplied deadline and swaps to
 *                  a "we're live" state at zero
 *   • gallery    – story-style auto-advancing slides with hold-to-peek,
 *                  pointer parallax, swipe and keyboard support
 *   • dialogs    – Impressum / Datenschutz via the native <dialog> element
 *   • password   – show / hide toggle for the storefront password field
 *
 * Everything degrades gracefully: without JavaScript the first slide, the
 * password form and the newsletter form all still work.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function prefersReducedMotion() {
    return reduceMotion.matches;
  }

  /* ----------------------------------------------------------------------- *
   * Countdown
   * ----------------------------------------------------------------------- */
  function initCountdown(root, scope) {
    var clock = root.querySelector('[data-pkn-countdown-clock]');
    var live = root.querySelector('[data-pkn-countdown-live]');
    var announce = root.querySelector('[data-pkn-countdown-announce]');
    var labelEl = root.querySelector('[data-pkn-countdown-label]');
    var units = {};
    var unitLabels = {};
    var lastValues = {};

    ['days', 'hours', 'minutes', 'seconds'].forEach(function (unit) {
      var el = root.querySelector('[data-pkn-unit="' + unit + '"]');
      units[unit] = el;
      var label = el && el.parentNode.querySelector('.pkn-eal__unit-label');
      unitLabels[unit] = label ? label.textContent.trim() : unit;
    });

    // Liquid validates and renders each deadline as a Unix timestamp in the
    // shop timezone, so the phase boundaries are the same for every visitor
    // wherever they are. 0 means "not configured".
    function epochOf(name) {
      var value = parseInt(root.dataset[name], 10);
      return Number.isFinite(value) && value > 0 ? value * 1000 : 0;
    }

    var phase1Ms = epochOf('phase1Deadline');
    var phase2Ms = epochOf('phase2Deadline');

    if (!phase1Ms) {
      root.hidden = true;
      return;
    }

    // 1 = before early access, 2 = early access running, 3 = public release.
    function phaseFor(nowMs) {
      if (nowMs < phase1Ms) return 1;
      if (phase2Ms && nowMs < phase2Ms) return 2;
      return 3;
    }

    function deadlineFor(phase) {
      if (phase === 1) return phase1Ms;
      if (phase === 2) return phase2Ms;
      return 0;
    }

    var phase = parseInt(root.dataset.phase, 10) || 1;
    var deadlineMs = deadlineFor(phase);

    var hasLiveContent = live && live.textContent.trim().length > 0;
    var lastAnnouncedMinute = null;
    var timer = null;

    // Swap the headline stack and the newsletter intro for the phase that is
    // starting. Both live in inert <template>s, so only the phase on screen
    // ever has an <h1> in the document.
    function applyPhase(next) {
      phase = next;
      root.dataset.phase = String(next);

      var copyTarget = scope.querySelector('[data-pkn-copy]');
      var copyTpl = scope.querySelector('[data-pkn-copy-phase="' + next + '"]');
      if (copyTarget && copyTpl) {
        copyTarget.replaceChildren(copyTpl.content.cloneNode(true));
      }

      var newsTarget = scope.querySelector('[data-pkn-newsletter-copy]');
      var newsTpl = scope.querySelector('[data-pkn-newsletter-phase="' + next + '"]');
      if (newsTarget && newsTpl) {
        newsTarget.replaceChildren(newsTpl.content.cloneNode(true));
      }

      // "Come back at 2pm" must not still be on screen at 2pm, so the password
      // message is swapped out the moment early access opens. One way only:
      // the template is dropped once used.
      if (next !== 1) {
        var pwAlert = scope.querySelector('[data-pkn-password-alert]');
        var pwTpl = scope.querySelector('[data-pkn-password-alert-phase]');
        if (pwAlert && pwTpl) {
          pwAlert.replaceWith(pwTpl.content.cloneNode(true));
          pwTpl.remove();
        }
      }

      if (labelEl) {
        if (next === 2) labelEl.textContent = root.dataset.phase2Label || '';
        else if (next === 3) labelEl.textContent = '';
        labelEl.hidden = !labelEl.textContent.trim();
      }

      deadlineMs = deadlineFor(next);
      lastAnnouncedMinute = null;
      // Repaint every digit rather than leaving the previous phase's values.
      lastValues = {};
    }

    function paint(unit, value) {
      var el = units[unit];
      if (!el) return;

      var text = value < 10 ? '0' + value : String(value);
      if (lastValues[unit] === text) return;

      lastValues[unit] = text;
      el.textContent = text;

      if (!prefersReducedMotion()) {
        el.classList.remove('pkn-eal__num--tick');
        // Force a reflow so the animation restarts on every change.
        void el.offsetWidth;
        el.classList.add('pkn-eal__num--tick');
      }
    }

    function goLive() {
      if (timer) clearInterval(timer);
      timer = null;

      if (hasLiveContent) {
        if (clock) clock.hidden = true;
        live.hidden = false;
      }
      root.setAttribute('data-state', 'live');
    }

    function tick() {
      var now = Date.now();

      // A phase can end while the page is open — and the HTML may have been
      // served from cache before the boundary — so trust the clock, not the
      // phase Liquid rendered.
      var current = phaseFor(now);
      if (current !== phase) {
        applyPhase(current);
      }

      if (!deadlineMs) {
        goLive();
        return;
      }

      var remaining = deadlineMs - now;

      if (remaining <= 0) {
        ['days', 'hours', 'minutes', 'seconds'].forEach(function (unit) {
          paint(unit, 0);
        });
        goLive();
        return;
      }

      var totalSeconds = Math.floor(remaining / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      paint('days', days);
      paint('hours', hours);
      paint('minutes', minutes);
      paint('seconds', seconds);

      // Announce once a minute instead of once a second so screen reader
      // users are not flooded by the ticking clock.
      if (announce && lastAnnouncedMinute !== minutes) {
        lastAnnouncedMinute = minutes;
        announce.textContent = [
          days + ' ' + unitLabels.days,
          hours + ' ' + unitLabels.hours,
          minutes + ' ' + unitLabels.minutes
        ].join(', ');
      }
    }

    tick();
    if (deadlineMs > Date.now()) timer = setInterval(tick, 1000);

    // Recover from a stale clock after the tab has been backgrounded.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && timer) tick();
    });
  }

  /* ----------------------------------------------------------------------- *
   * Gallery
   * ----------------------------------------------------------------------- */
  function initGallery(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-pkn-slide]'));
    if (slides.length === 0) return;

    var bars = Array.prototype.slice.call(root.querySelectorAll('[data-pkn-bar]'));
    var status = root.querySelector('[data-pkn-gallery-status]');
    var playPause = root.querySelector('[data-pkn-playpause]');
    var playPauseLabel = root.querySelector('[data-pkn-playpause-label]');
    var prev = root.querySelector('[data-pkn-prev]');
    var next = root.querySelector('[data-pkn-next]');

    var interval = parseInt(root.dataset.interval, 10) || 5000;
    var wantsAutoplay = root.dataset.autoplay === 'true' && slides.length > 1;
    var wantsKenBurns = root.dataset.kenburns === 'true';
    var wantsParallax = root.dataset.parallax === 'true';

    var index = 0;
    var timer = null;
    // `paused` is the visitor's explicit choice, `suspended` is temporary
    // (hover, hidden tab, an open dialog). Both stop the timer.
    var paused = prefersReducedMotion();
    var suspended = false;

    var labels = {
      pause: playPauseLabel ? playPauseLabel.textContent.trim() : '',
      play: root.dataset.labelPlay || ''
    };

    function syncMotionClasses() {
      root.classList.toggle('is-animated', wantsKenBurns && !prefersReducedMotion());
      root.classList.toggle('is-static', !wantsAutoplay || prefersReducedMotion());
    }

    function render() {
      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      bars.forEach(function (bar, i) {
        bar.classList.toggle('is-done', i < index);
        bar.classList.remove('is-active');
      });

      if (bars[index]) {
        // Restart the fill animation from zero for the new slide.
        void bars[index].offsetWidth;
        bars[index].classList.add('is-active');
      }

      if (status) {
        status.textContent = index + 1 + ' / ' + slides.length;
      }
    }

    function stop() {
      if (timer) clearTimeout(timer);
      timer = null;
      root.classList.add('is-paused');
    }

    function schedule() {
      stop();
      if (!wantsAutoplay || paused || suspended || prefersReducedMotion()) return;
      root.classList.remove('is-paused');
      timer = setTimeout(function () {
        go(index + 1);
      }, interval);
    }

    function go(target) {
      index = (target + slides.length) % slides.length;
      render();
      schedule();
    }

    function suspend(value) {
      suspended = value;
      if (value) {
        stop();
      } else {
        schedule();
      }
    }

    function setPaused(value) {
      paused = value;
      if (playPause) {
        playPause.setAttribute('aria-pressed', value ? 'true' : 'false');
        if (playPauseLabel && labels.play) {
          playPauseLabel.textContent = value ? labels.play : labels.pause;
        }
      }
      schedule();
    }

    /* -- Peek (hold to reveal a blurred teaser) --------------------------- */
    var peekTimer = null;

    function setPeek(value) {
      root.classList.toggle('is-peeking', value);
      if (value) root.classList.add('has-interacted');
    }

    /* -- Interaction ------------------------------------------------------ */
    if (prev) {
      prev.addEventListener('click', function () {
        root.classList.add('has-interacted');
        setPaused(true);
        go(index - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        root.classList.add('has-interacted');
        setPaused(true);
        go(index + 1);
      });
    }

    if (playPause) {
      labels.play = playPause.dataset.labelPlay || labels.play;
      playPause.addEventListener('click', function () {
        setPaused(!paused);
      });
    }

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPaused(true);
        go(index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPaused(true);
        go(index + 1);
      }
    });

    if (finePointer.matches) {
      root.addEventListener('mouseenter', function () {
        suspend(true);
        setPeek(true);
      });
      root.addEventListener('mouseleave', function () {
        suspend(false);
        setPeek(false);
      });
    }

    root.addEventListener('focusin', function () {
      suspend(true);
    });
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) suspend(false);
    });

    document.addEventListener('visibilitychange', function () {
      suspend(document.hidden);
    });

    /* -- Touch: hold to peek, swipe to navigate --------------------------- */
    var pointerStart = null;

    root.addEventListener(
      'pointerdown',
      function (event) {
        if (event.pointerType === 'mouse') return;
        pointerStart = { x: event.clientX, y: event.clientY, time: Date.now() };
        suspend(true);
        peekTimer = setTimeout(function () {
          setPeek(true);
        }, 140);
      },
      { passive: true }
    );

    function endPointer(event) {
      if (event.pointerType === 'mouse' || !pointerStart) return;

      if (peekTimer) clearTimeout(peekTimer);
      peekTimer = null;
      setPeek(false);

      var dx = event.clientX - pointerStart.x;
      var dy = event.clientY - pointerStart.y;
      var elapsed = Date.now() - pointerStart.time;
      pointerStart = null;

      // Horizontal flick → navigate. Anything else was a hold or a page scroll.
      if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5 && elapsed < 800) {
        root.classList.add('has-interacted');
        setPaused(true);
        go(dx < 0 ? index + 1 : index - 1);
        return;
      }

      suspend(false);
    }

    root.addEventListener('pointerup', endPointer, { passive: true });
    root.addEventListener('pointercancel', endPointer, { passive: true });

    /* -- Pointer parallax -------------------------------------------------- */
    if (wantsParallax && finePointer.matches && !prefersReducedMotion()) {
      var frame = null;

      root.addEventListener('pointermove', function (event) {
        if (event.pointerType !== 'mouse' || frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var box = root.getBoundingClientRect();
          if (!box.width || !box.height) return;
          var x = (event.clientX - box.left) / box.width - 0.5;
          var y = (event.clientY - box.top) / box.height - 0.5;
          root.style.setProperty('--pkn-eal-px', (x * -16).toFixed(2) + 'px');
          root.style.setProperty('--pkn-eal-py', (y * -16).toFixed(2) + 'px');
        });
      });

      root.addEventListener('pointerleave', function () {
        root.style.setProperty('--pkn-eal-px', '0px');
        root.style.setProperty('--pkn-eal-py', '0px');
      });
    }

    /* -- Motion preference can change while the page is open -------------- */
    var onMotionChange = function () {
      syncMotionClasses();
      setPaused(prefersReducedMotion());
    };

    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', onMotionChange);
    } else if (typeof reduceMotion.addListener === 'function') {
      reduceMotion.addListener(onMotionChange);
    }

    syncMotionClasses();
    setPaused(paused);
    render();
    schedule();

    root.pknSuspend = suspend;
  }

  /* ----------------------------------------------------------------------- *
   * Legal dialogs
   * ----------------------------------------------------------------------- */
  function initDialogs(scope) {
    function suspendGallery(value) {
      var gallery = scope.querySelector('[data-pkn-gallery]');
      if (gallery && typeof gallery.pknSuspend === 'function') gallery.pknSuspend(value);
    }

    scope.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-pkn-dialog-open]');
      if (opener) {
        var dialog = document.getElementById(opener.getAttribute('data-pkn-dialog-open'));
        if (!dialog) return;
        event.preventDefault();
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          // Very old browsers: degrade to a plain, centred open panel.
          dialog.setAttribute('open', '');
        }
        suspendGallery(true);
        return;
      }

      var closer = event.target.closest('[data-pkn-dialog-close]');
      if (closer) {
        var owner = closer.closest('dialog');
        if (!owner) return;
        event.preventDefault();
        if (typeof owner.close === 'function') {
          owner.close();
        } else {
          owner.removeAttribute('open');
        }
      }
    });

    // Clicking the backdrop closes the dialog: the <dialog> box itself only
    // covers its own rect, so a click landing outside it is a backdrop click.
    Array.prototype.forEach.call(scope.querySelectorAll('.pkn-eal-dialog'), function (dialog) {
      dialog.addEventListener('close', function () {
        suspendGallery(false);
      });

      dialog.addEventListener('click', function (event) {
        if (event.target !== dialog) return;
        var box = dialog.getBoundingClientRect();
        var outside =
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom;
        if (outside) dialog.close();
      });
    });
  }

  /* ----------------------------------------------------------------------- *
   * Password field
   * ----------------------------------------------------------------------- */
  function initPasswordToggle(scope) {
    var toggle = scope.querySelector('[data-pkn-password-toggle]');
    var input = scope.querySelector('[data-pkn-password-input]');
    var label = scope.querySelector('[data-pkn-password-toggle-label]');
    if (!toggle || !input) return;

    var showLabel = label ? label.textContent.trim() : '';
    var hideLabel = toggle.dataset.labelHide || showLabel;

    toggle.addEventListener('click', function () {
      var reveal = input.type === 'password';
      input.type = reveal ? 'text' : 'password';
      toggle.setAttribute('aria-pressed', reveal ? 'true' : 'false');
      if (label) label.textContent = reveal ? hideLabel : showLabel;
      input.focus({ preventScroll: true });
    });
  }

  /* ----------------------------------------------------------------------- *
   * Boot
   * ----------------------------------------------------------------------- */
  function init(scope) {
    if (!scope || scope.dataset.pknEalReady === 'true') return;
    scope.dataset.pknEalReady = 'true';

    var countdown = scope.querySelector('[data-pkn-countdown]');
    if (countdown) initCountdown(countdown, scope);

    var gallery = scope.querySelector('[data-pkn-gallery]');
    if (gallery) initGallery(gallery);

    initDialogs(scope);
    initPasswordToggle(scope);

    // layout/password.liquid ships a "Enter using password" button plus a
    // modal that auto-opens on a wrong password. This section renders its own
    // password field and its own inline error, so drop the id the layout's
    // script looks for (the button itself is hidden by the section's CSS).
    var legacyToggle = document.getElementById('logInWithPasswordBtn');
    if (legacyToggle) legacyToggle.removeAttribute('id');

    // A successful newsletter sign-up reloads the page; move focus to the
    // confirmation so the visitor lands on the thing that just changed.
    var success = scope.querySelector('[data-pkn-newsletter-success]');
    if (success) {
      success.focus({ preventScroll: false });
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.pkn-eal'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target.querySelector ? event.target.querySelector('.pkn-eal') : null;
    if (scope) init(scope);
  });
})();
